import { create } from "@bufbuild/protobuf";
import isURL from "validator/lib/isURL";
import { GetLinkRequestSchema, PrivacyLinkSchema, type CustomPolicyLink, type Domain, type PrivacyLink } from "#gen/links/v1/links_pb.js";
import { getLink } from "#lib/links.js";
import { Logger } from "#lib/logging.js";
import { getPrivacySmileUrl } from "#lib/privacy.js";
import type { ValidationResponse } from "#lib/validation.js";

const validStatuses = [200, 301, 302];

// The order predefined footer links render in when a domain has no explicit privacy_links.
// Published pages depend on it, so never reorder these keys. Keep in sync with the
// links-service validator's predefinedFooterLinkKeys.
const LEGACY_FOOTER_ORDER = ["privacy_policy", "cookie_preferences", "do_not_sell", "privacy_choices"];

export const PRIVACY_POLICY_LABEL = "Privacy policy";
// The OneTrust SDK binds its "cookie settings" trigger to this element id.
const COOKIE_PREFERENCES_BUTTON_ID = "ot-sdk-btn";

export type FooterLinkItem = { id: string; label: string } & ({ element: "a"; href: string } | { element: "button"; buttonId: string });

export type FooterLinkDomainInfo = {
	domain: string;
	privacyPolicyLinkHash: string;
	privacySmileUrl: string;
	privacyPolicyLinkLabel: string;
	cookiePreferencesLinkLabel: string;
	doNotSellPolicyLinkUrl: string;
	doNotSellPolicyLinkLabel: string;
	privacyPreferencesUrl: string;
	privacyPreferencesLabel: string;
	privacyLinks: PrivacyLink[];
};

// Privacy policy is always eligible; the other predefined links only render when their
// backing scalar fields are set.
function eligiblePredefinedLinks(d: FooterLinkDomainInfo): Map<string, FooterLinkItem> {
	const m: Map<string, FooterLinkItem> = new Map();
	m.set("privacy_policy", { id: "privacy_policy", label: d.privacyPolicyLinkLabel.trim() || PRIVACY_POLICY_LABEL, element: "a", href: `https://${getPrivacySmileUrl(d)}` });
	if (d.cookiePreferencesLinkLabel.trim().length > 0) {
		m.set("cookie_preferences", { id: "cookie_preferences", label: d.cookiePreferencesLinkLabel, element: "button", buttonId: COOKIE_PREFERENCES_BUTTON_ID });
	}
	if (d.doNotSellPolicyLinkUrl.trim().length > 0 && d.doNotSellPolicyLinkLabel.trim().length > 0) {
		m.set("do_not_sell", { id: "do_not_sell", label: d.doNotSellPolicyLinkLabel, element: "a", href: d.doNotSellPolicyLinkUrl });
	}
	if (d.privacyPreferencesUrl.trim().length > 0 && d.privacyPreferencesLabel.trim().length > 0) {
		m.set("privacy_choices", { id: "privacy_choices", label: d.privacyPreferencesLabel, element: "a", href: d.privacyPreferencesUrl });
	}
	return m;
}

// The single source of footer link order, shared by the viewer and the drawer reorder UI.
// An empty privacy_links falls back to LEGACY_FOOTER_ORDER so existing pages are unchanged.
export function buildFooterLinkList(d: FooterLinkDomainInfo): FooterLinkItem[] {
	const eligible = eligiblePredefinedLinks(d);
	const result: FooterLinkItem[] = [];
	const used: Set<string> = new Set();
	function push(item: FooterLinkItem) {
		if (!used.has(item.id)) {
			result.push(item);
			used.add(item.id);
		}
	}

	if (!d.privacyLinks || d.privacyLinks.length === 0) {
		for (const key of LEGACY_FOOTER_ORDER) {
			const item = eligible.get(key);
			if (item) push(item);
		}
		return result;
	}

	for (const pl of d.privacyLinks) {
		if (pl.link.case === "custom") {
			const c = pl.link.value;
			if (c.url.trim().length > 0 && c.label.trim().length > 0) {
				push({ id: c.id, label: c.label, element: "a", href: c.url });
			}
		} else if (pl.link.case === "predefinedKey") {
			const item = eligible.get(pl.link.value);
			if (item) push(item);
		}
	}
	// Append any eligible predefined link the array didn't reference, so enabling one later never drops it.
	for (const [, item] of eligible) push(item);
	return result;
}

// Rebuilds privacy_links from the drawer's working order (predefined keys + custom ids) and
// the current custom data. Order refs to stale or deleted customs are dropped, and any custom
// not yet in the order is appended so a newly added link still renders.
export function assemblePrivacyLinks(order: string[], customs: CustomPolicyLink[]): PrivacyLink[] {
	const customById = new Map(customs.map(c => [c.id, c]));
	const seen: Set<string> = new Set();
	const out: PrivacyLink[] = [];
	for (const id of order) {
		if (seen.has(id)) continue;
		if (LEGACY_FOOTER_ORDER.includes(id)) {
			seen.add(id);
			out.push(create(PrivacyLinkSchema, { link: { case: "predefinedKey", value: id } }));
		} else {
			const c = customById.get(id);
			if (c) {
				seen.add(id);
				out.push(create(PrivacyLinkSchema, { link: { case: "custom", value: c } }));
			}
		}
	}
	for (const c of customs) {
		if (!seen.has(c.id)) {
			seen.add(c.id);
			out.push(create(PrivacyLinkSchema, { link: { case: "custom", value: c } }));
		}
	}
	return out;
}

// Mirrors the links-service validator for custom policy links: the URL must be an
// absolute http(s) URL (server does url.ParseRequestURI + an http/https scheme check).
// require_protocol catches the common "example.com" mistake that the server rejects;
// require_tld is relaxed so staging/edge hosts the server would accept aren't blocked.
export function isValidCustomPolicyLinkUrl(url: string): boolean {
	const trimmed = url.trim();
	if (!isURL(trimmed, { require_protocol: true, protocols: ["http", "https"], require_tld: false })) {
		return false;
	}
	// isURL strips a #fragment before validating, but the server's url.ParseRequestURI treats a
	// '#' in the authority as an invalid host. Reject a '#' that lands before any path or query.
	const authority = trimmed.replace(/^https?:\/\//i, "").split(/[/?]/, 1)[0];
	return !authority.includes("#");
}

// An incomplete row (missing a label or url) is dropped on save, so it never blocks the save;
// only a row with both fields filled in is held to a valid http(s) URL.
function isCustomPolicyLinkRowValid(link: { label: string; url: string }): boolean {
	const label = link.label.trim();
	const url = link.url.trim();
	if (label.length === 0 || url.length === 0) {
		return true;
	}
	return isValidCustomPolicyLinkUrl(url);
}

export function areCustomPolicyLinksValid(links: { label: string; url: string }[]): boolean {
	return links.every(isCustomPolicyLinkRowValid);
}

export const INPUT_DEBOUNCE_DELAY = 750;

export async function validateURL(url: string, abortController?: AbortController): Promise<ValidationResponse> {
	if (url === "" || !isURL(url)) {
		return {
			status: 0,
			error: "Please enter a valid URL",
		};
	}
	const error = "Failed to validate URL. Please try again.";
	try {
		abortController?.abort();
		abortController = new AbortController();
		const res = await fetch(`/api/validate?validate=true&url=${encodeURIComponent(url)}`, {
			method: "GET",
			signal: abortController.signal,
		});
		const validation = (await res.json()) as ValidationResponse;
		if (!("status" in validation)) {
			return {
				status: 0,
				error,
			};
		} else {
			return validation;
		}
	} catch (err) {
		Logger.error(`Failed to validate URL: ${url}`, { err });
		return {
			status: 0,
			error,
		};
	}
}

export function doesDomainHaveCookiePreferencesButton(domain: Domain): boolean {
	return domain.cookiePreferencesLinkLabel.trim().length > 0;
}

export function doesDomainHaveDoNotSellLink(domain: Domain): boolean {
	return domain.doNotSellPolicyLinkLabel.trim().length > 0 && domain.doNotSellPolicyLinkUrl.trim().length > 0;
}

export function doesDomainHavePrivacyChoicesLink(domain: Domain): boolean {
	return domain.privacyPreferencesUrl.trim().length > 0 && domain.privacyPreferencesLabel.trim().length > 0;
}

export function isDomainDPACompliant(
	domain:
		| Domain
		| {
				domain: string;
				oneTrustId: string;
				privacyPolicyLinkHash: string;
				cookiePreferencesLinkLabel: string;
				doNotSellPolicyLinkLabel: string;
				doNotSellPolicyLinkUrl: string;
				privacyPreferencesLabel: string;
				privacyPreferencesUrl: string;
		  }
		| undefined,
): boolean {
	if (!domain) {
		return false;
	}
	return domain.oneTrustId.trim().length > 0 && domain.privacyPolicyLinkHash.trim().length > 0 && domain.privacyPreferencesUrl.trim().length > 0 && domain.privacyPreferencesLabel.trim().length > 0;
}

export function isCookiePreferencesButtonValid(isEnabled: boolean, label: string): { isValid: boolean; error: string | null } {
	if (isEnabled && label.trim().length === 0) {
		return { isValid: false, error: "This field is required when enabling the cookie preferences link." };
	} else {
		return { isValid: true, error: null };
	}
}

export async function isLinkAndLabelComboValid(isEnabled: boolean, url: string, label: string, knownValidationState?: boolean): Promise<{ isValid: boolean; linkUrlError: string | null; linkLabelError: string | null }> {
	let isValid = true;
	let linkUrlError: string | null;
	let linkLabelError: string | null;

	if (isEnabled) {
		if (url.trim().length === 0) {
			linkUrlError = "This field is required.";
			isValid = false;
		} else {
			// If we can, use the known validation state to skip re-validation
			if (knownValidationState === true) {
				linkUrlError = null;
			} else if (knownValidationState === false) {
				linkUrlError = "Please enter a valid URL.";
				isValid = false;
			} else {
				const isValidUrl = await validateURL(url);
				if (validStatuses.includes(isValidUrl.status)) {
					linkUrlError = null;
				} else {
					linkUrlError = "Please enter a valid URL.";
					isValid = false;
				}
			}
		}
		if (label.trim().length === 0) {
			linkLabelError = "This field is required.";
			isValid = false;
		} else {
			linkLabelError = null;
		}
	} else {
		linkUrlError = null;
		linkLabelError = null;
	}

	return { isValid, linkUrlError, linkLabelError };
}

export async function getPrivacyPolicyUrl(hash: string, domainId: string): Promise<string | null> {
	try {
		const res = await getLink(
			create(GetLinkRequestSchema, {
				hash,
				domainId,
			}),
		);
		return res.original;
	} catch (e) {
		Logger.error("Failed to get privacy policy URL", { err: e });
		return null;
	}
}
